import React, { ReactNode } from "react"
import clsx from "clsx"
import { Absolute, Row } from "ui/components/atoms/_atoms"
import Icon from "ui/components/atoms/icon"
import Spinner from "ui/components/atoms/spinner"
import { BaseStyles } from "ui/types/base-styles"
import { generateStylesFromProps } from "ui/utils/styles"

import styles from "./Button.module.css"

export type ButtonProps = StyledButtonProps & {
  children: ReactNode
  icon?: string
  endIcon?: string
  submit?: boolean
  onClick?: () => void
  ariaLabel?: string
  form?: string
  loading?: boolean
  $type?: "primary" | "secondary" | "third" | "fourth"
  $hoverColor?: string
  $hoverBackground?: string
  $padding?: string
  $boxShadow?: boolean
}

export type StyledButtonProps = BaseStyles & {
  icon?: string
  submit?: boolean
  onClick?: () => void
  disabled?: boolean
  $loading?: boolean
  $type?: "primary" | "secondary" | "third" | "fourth"
  $styledType?: "primary" | "secondary" | "third" | "fourth"
  $hoverColor?: string
  $hoverBackground?: string
  $padding?: string
  $boxShadow?: boolean
}

const Button = ({
  children,
  icon,
  endIcon,
  submit,
  onClick,
  loading,
  ariaLabel,
  disabled,
  form,
  $type = "primary",
  $padding = "12px 20px",
  $hoverColor,
  $hoverBackground,
  $boxShadow = false,
  className = "",
  $background,
  style = {},
  ...rest
}: ButtonProps & { className?: string; style?: React.CSSProperties }) => {
  const generatedStyles = generateStylesFromProps({ ...rest, $padding, $background })

  const buttonClasses = clsx(
    styles.button,
    styles[$type],
    {
      [styles.loading]: loading,
      [styles.boxShadow]: $boxShadow,
    },
    className,
  )

  const buttonStyles = {
    ...generatedStyles,
    ...style,
  }

  const iconMarginSize = $type === "third" || $type === "fourth" ? "XS" : "20px"

  return (
    <button
      aria-label={ariaLabel}
      type={submit ? "submit" : "button"}
      onClick={onClick}
      disabled={disabled}
      form={form}
      className={buttonClasses}
      style={buttonStyles}
      data-hover-background={$hoverBackground}
      data-hover-color={$hoverColor}
    >
      <Row $maxWidth="100%" $alignItems="center" $opacity={loading ? 0 : 1} $margin="0 auto">
        {icon && <Icon icon={icon} $marginRight={iconMarginSize} />}
        <span className={styles.span}>{children}</span>
        {endIcon && <Icon icon={endIcon} $marginLeft={iconMarginSize} />}
      </Row>
      {loading && (
        <Absolute
          $width="100%"
          $height="100%"
          $left="0"
          $top="0"
          $display="flex"
          $alignItems="center"
          $justifyContent="center"
        >
          <Spinner $color={$type === "primary" ? "white" : "primary"} $size="20px" />
        </Absolute>
      )}
    </button>
  )
}

export default Button
